import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import { Select, Option } from 'carbon-react/lib/components/select';

import { toastError } from '@futrli/ui-components';
import { Organisation } from '@futrli-sdk/organisations';
import { Logger, QUERY_PARAMS } from '@futrli/shared-services';
import { fetchOrganisations } from '@futrli/organisations';
import { getOnboardingInitialSyncUrl } from '@futrli/shared-services/onboarding/utils/route-utils';

import { OnboardingNavbar } from './onboarding-navbar';
import * as styles from './onboarding.scss';
import { OnboardingLoader } from './onboarding-loader';

export const OnboardingSageOrgs = () => {
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('');
  const [selectedDataSourceId, setSelectedDataSourceId] = useState('');
  const [organisations, setOrganisations] = useState<Organisation[]>();
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const type = searchParams.get(QUERY_PARAMS.TYPE);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!workspaceId || !type) {
        return;
      }
      try {
        setIsLoading(true);
        const fetchedOrgs = await fetchOrganisations(workspaceId);
        setOrganisations(fetchedOrgs.filter(o => o.datasource.type === 'sage'));
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [workspaceId, type]);

  const handleOrgChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedOrganisationId(event.target.value);
      setSelectedDataSourceId(organisations?.find(o => o.id === event.target.value)?.datasource.id || '');
    },
    [organisations]
  );

  const handleNextClick = useCallback(() => {
    if (!workspaceId || !selectedOrganisationId || !selectedDataSourceId || !type) {
      return;
    }

    navigate(getOnboardingInitialSyncUrl(workspaceId, selectedOrganisationId, selectedDataSourceId, type));
  }, [navigate, selectedDataSourceId, selectedOrganisationId, type, workspaceId]);

  if (!workspaceId || !type) {
    return null;
  }

  if (isLoading) {
    return <OnboardingLoader />;
  }

  return (
    <>
      <OnboardingNavbar />
      <div className={styles.container}>
        <div style={{ height: 200 }} />
        <div className={styles.title} style={{ marginBottom: 30 }}>
          <FormattedMessage
            id="onboarding.added_more_than_one_sage_org"
            defaultMessage="You've added more than one Sage organisation."
          />
        </div>
        <div className={styles.subTitle} style={{ marginBottom: 30 }}>
          <FormattedMessage
            id="onboarding.choose_org_for_onboarding"
            defaultMessage="Choose which organisation you'd like to be onboarded into."
          />
        </div>
        <Box width="623px" marginBottom="40px">
          <Select
            size="large"
            value={selectedOrganisationId}
            autoComplete="off"
            label={intl.formatMessage({
              id: 'onboarding.choose_which_organisation',
              defaultMessage: 'Choose which organisaion'
            })}
            onChange={handleOrgChange}
            disabled={!organisations}
          >
            {organisations && organisations.map(o => <Option key={o.id} value={o.id} text={o.name} />)}
          </Select>
        </Box>
        <Button
          size="large"
          buttonType="primary"
          disabled={!selectedOrganisationId || !selectedDataSourceId || !workspaceId || !type}
          onClick={handleNextClick}
        >
          <FormattedMessage id="common.lets_go" defaultMessage="Let's go!" />
        </Button>
      </div>
    </>
  );
};
